import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../api/client';
import { useAnalyses } from '../analyses/AnalysesContext';
import { useAuth } from '../auth/AuthContext';
import Uploader from '../components/Uploader';
import AnalysisGroup from '../components/AnalysisGroup';

function groupByRoot(list) {
  const byId = new Map();
  for (const a of list) byId.set(a.id, a);

  // Audio-File-Identität als Fallback-Schlüssel, falls source_analysis_id fehlt
  // (z.B. wenn der ursprüngliche Primary gelöscht wurde und Secondaries via
  // ON DELETE SET NULL ihre Verbindung verloren haben).
  const audioKey = (a) =>
    `audio:${a.original_filename}|${a.audio_size_bytes}|${a.audio_duration_seconds || 0}`;

  const rootKey = (a) => {
    // Eine Secondary nutzt den audio-key ihrer Primary, damit alle Schwestern
    // im gleichen Bucket landen wie eventuell weitere Standalone-Analysen
    // mit identischem Audio.
    if (a.source_analysis_id && byId.has(a.source_analysis_id)) {
      return audioKey(byId.get(a.source_analysis_id));
    }
    return audioKey(a);
  };

  const map = new Map();
  for (const a of list) {
    const key = rootKey(a);
    if (!map.has(key)) map.set(key, []);
    map.get(key).push(a);
  }

  // Items innerhalb einer Gruppe: alphabetisch nach LLM-Name
  for (const arr of map.values()) {
    arr.sort((x, y) => {
      const xm = (x.model_used || x.model_requested || '').toLowerCase();
      const ym = (y.model_used || y.model_requested || '').toLowerCase();
      return xm.localeCompare(ym);
    });
  }
  // Gruppen sortiert: neueste Analyse-in-der-Gruppe zuerst
  return [...map.values()].sort((a, b) => {
    const aMax = Math.max(...a.map((x) => new Date(x.created_at).getTime()));
    const bMax = Math.max(...b.map((x) => new Date(x.created_at).getTime()));
    return bMax - aMax;
  });
}

export default function DashboardPage() {
  const { items, userFilter, setFilter } = useAnalyses();
  const { user, logout } = useAuth();
  const isAdmin = user.role === 'admin';

  const live = items.filter(
    (a) => a.status === 'queued' || a.status === 'processing'
  );
  const history = items.filter(
    (a) => a.status === 'completed' || a.status === 'failed'
  );

  // Für Admin: deduplizierte User-Liste aus den sichtbaren Analysen,
  // alphabetisch nach Display-Name.
  const userOptions = useMemo(() => {
    if (!isAdmin) return [];
    const map = new Map();
    for (const a of items) {
      if (a.user && !map.has(a.user.id)) {
        map.set(a.user.id, a.user);
      }
    }
    return [...map.values()].sort((x, y) =>
      x.display_name.localeCompare(y.display_name)
    );
  }, [isAdmin, items]);

  const liveGroups = useMemo(() => groupByRoot(live), [live]);
  const historyGroups = useMemo(() => groupByRoot(history), [history]);

  // Modell-Liste einmalig laden – wird an Gruppen für 'weitere Analyse' gereicht
  const [allModels, setAllModels] = useState([]);
  useEffect(() => {
    api.get('/models').then((r) => setAllModels(r.data.models)).catch(() => {});
  }, []);

  return (
    <div className="dashboard">
      <header>
        <h1>Emma DentAssist</h1>
        <div className="user-menu">
          <span>{user.display_name}</span>
          {isAdmin && (
            <>
              {' · '}
              <Link to="/admin/invites">Einladungen</Link>
              {' · '}
              <Link to="/admin/users">Benutzer</Link>
              {' · '}
              <Link to="/admin/settings">Einstellungen</Link>
            </>
          )}
          {' · '}
          <button onClick={logout} className="linklike">
            Logout
          </button>
        </div>
      </header>

      <Uploader />

      {isAdmin && userOptions.length > 1 && (
        <div className="filter-bar">
          <label>
            Nutzer:{' '}
            <select
              value={userFilter || ''}
              onChange={(e) => setFilter(e.target.value || null)}
            >
              <option value="">Alle Nutzer</option>
              {userOptions.map((u) => (
                <option key={u.id} value={u.id}>
                  {u.display_name} ({u.email})
                </option>
              ))}
            </select>
          </label>
        </div>
      )}

      {liveGroups.length > 0 && (
        <section>
          <h2>Laufend</h2>
          {liveGroups.map((group) => (
            <AnalysisGroup key={group[0].id} items={group} showUser={isAdmin} allModels={allModels} />
          ))}
        </section>
      )}

      <section>
        <h2>Historie{userFilter ? ' (gefiltert)' : ''}</h2>
        {historyGroups.length === 0 && <p className="meta">Noch keine Analysen.</p>}
        {historyGroups.map((group) => (
          <AnalysisGroup key={group[0].id} items={group} showUser={isAdmin} allModels={allModels} />
        ))}
      </section>
    </div>
  );
}
